import { useState } from "react";
import clsx from "clsx";
import { FlaskIcon, LockIcon } from "@/shared/icons";

type Props = {
  colorOne: string;
  colorTwo: string;
  level: 0 | 1 | 2 | 3;
  onClick: () => void;
  isLocked?: boolean;
};

export default function FactionUpgradeNode({
  colorOne,
  colorTwo,
  level,
  onClick,
  isLocked,
}: Props) {
  const [hover, setHover] = useState(false);

  return (
    <button
      type="button"
      className={clsx(
        "group relative flex justify-center items-center size-[4.375rem] cursor-pointer",
        isLocked && "pointer-events-none",
      )}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
    >
      <svg width="70" height="70" viewBox="0 0 70 70">
        <defs>
          <linearGradient
            id="ringGradient"
            gradientUnits="userSpaceOnUse"
            x1="-33"
            y1="0"
            x2="33"
            y2="0"
          >
            <stop offset="0%" stopColor={colorOne} />
            <stop offset="100%" stopColor={colorTwo} />
          </linearGradient>
        </defs>
        <g transform="translate(35 35) rotate(110)" shapeRendering="geometricPrecision">
          {/* 1 */}
          <path
            d="M 33 0 A 33 33 0 0 1 -7.22 32.201"
            fill="none"
            stroke="#191919"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M 33 0 A 33 33 0 0 1 -7.22 32.201"
            fill="none"
            stroke="url(#ringGradient)"
            strokeWidth="4"
            strokeLinecap="round"
            style={{ opacity: level > 0 ? 1 : 0, transition: "opacity 300ms ease" }}
          />
          {/* 3 */}
          <path
            d="M -16.5 28.579 A 33 33 0 0 1 -24.277 -22.353"
            fill="none"
            stroke="#191919"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M -16.5 28.579 A 33 33 0 0 1 -24.277 -22.353"
            fill="none"
            stroke="url(#ringGradient)"
            strokeWidth="4"
            strokeLinecap="round"
            style={{ opacity: level > 2 ? 1 : 0, transition: "opacity 300ms ease" }}
          />
          {/* 2 */}
          <path
            d="M -16.5 -28.579 A 33 33 0 0 1 31.496 -9.848"
            fill="none"
            stroke="#191919"
            strokeWidth="4"
            strokeLinecap="round"
          />
          <path
            d="M -16.5 -28.579 A 33 33 0 0 1 31.496 -9.848"
            fill="none"
            stroke="url(#ringGradient)"
            strokeWidth="4"
            strokeLinecap="round"
            style={{ opacity: level > 1 ? 1 : 0, transition: "opacity 300ms ease" }}
          />
        </g>
      </svg>
      <div
        className="absolute top-1/2 left-1/2 flex justify-center items-center size-13 rounded-[50%] -translate-x-1/2 -translate-y-1/2"
        style={{ background: level < 3 ? "#191919" : null }}
      >
        <div
          className="absolute inset-0 rounded-[inherit] pointer-events-none"
          style={{
            background: `linear-gradient(180deg, ${colorOne}26 0%, ${colorOne}0F 50%, transparent 100%)`,
            opacity: level === 3 ? 1 : 0,
            transition: "opacity 300ms ease",
          }}
        />
        {isLocked ? (
          <LockIcon className="size-5" />
        ) : (
          <FlaskIcon
            className="size-5 transition-all duration-200"
            style={{ color: hover || level === 3 ? colorOne : "rgba(255, 255, 255, 0.60)" }}
          />
        )}
        <svg
          className="absolute inset-0 pointer-events-none"
          width="52"
          height="52"
          viewBox="0 0 52 52"
          style={{ opacity: level === 3 ? 1 : 0, transition: "opacity 300ms ease" }}
        >
          <defs>
            <linearGradient
              id="topArcFade"
              x1="0"
              y1="26"
              x2="52"
              y2="26"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0%" stopColor={colorOne} stopOpacity="0" />
              <stop offset="3%" stopColor={colorOne} stopOpacity="0.04" />
              <stop offset="14%" stopColor={colorOne} stopOpacity="0.42" />
              <stop offset="26%" stopColor={colorOne} stopOpacity="0.65" />
              <stop offset="40%" stopColor={colorOne} stopOpacity="1" />
              <stop offset="60%" stopColor={colorOne} stopOpacity="1" />
              <stop offset="74%" stopColor={colorOne} stopOpacity="0.65" />
              <stop offset="86%" stopColor={colorOne} stopOpacity="0.42" />
              <stop offset="97%" stopColor={colorOne} stopOpacity="0.04" />
              <stop offset="100%" stopColor={colorOne} stopOpacity="0" />
            </linearGradient>
            <mask id="ring2px">
              <rect width="52" height="52" fill="black" />
              <circle cx="26" cy="26" r="26" fill="white" />
              <circle cx="26" cy="26" r="24" fill="black" />
            </mask>
            <clipPath id="topHalf">
              <rect x="0" y="0" width="52" height="26" />
            </clipPath>
          </defs>
          <g clipPath="url(#topHalf)">
            <circle
              cx="26"
              cy="26"
              r="26"
              fill="url(#topArcFade)"
              mask="url(#ring2px)"
              shapeRendering="geometricPrecision"
            />
          </g>
        </svg>
      </div>
    </button>
  );
}
